/**
 * @file `ShellContext` — React context exposing shell-level concerns
 *       (toast, density) to panels without prop-drilling.
 * @module @labcraft/admin/ui/kernel/shell-context
 *
 * Panels read this via `useContext(ShellContext)` and call `toast(msg)`
 * for ephemeral feedback. The kernel owns the density preference
 * (operator-tool only) — theme moved to the shared `ThemeToggle`
 * inside `AccountMenu` (cookie at `.labcraft.dev`) and is no longer
 * an admin concern.
 */

import { createContext, type Context } from 'react'

/** Density preference; mirrors `[data-density]` on the html element. */
export type Density = 'compact' | 'cozy'

/** Shape exposed by {@link ShellContext}. */
export type ShellContextValue = {
  /** Fire a toast that auto-dismisses ~1700 ms later. */
  toast: (message: string) => void
  /** Current density; readable for chrome rendering. */
  density: Density
  /** Flip the density. The kernel persists to localStorage. */
  setDensity: (density: Density) => void
  /** Whether the ⌘K command palette overlay is open. */
  cmdkOpen: boolean
  /** Open/close the command palette overlay. */
  setCmdkOpen: (open: boolean) => void
  /**
   * Register the active panel's ⌘K handler. While a handler is set, ⌘K
   * defers to it instead of opening the generic palette; pass `null` on
   * unmount to restore the default. No panel registers one today — the
   * hook keeps the design's contract open for per-panel palettes.
   */
  registerCmdK: (handler: (() => void) | null) => void
}

const fallback: ShellContextValue = {
  toast: () => {},
  density: 'compact',
  setDensity: () => {},
  cmdkOpen: false,
  setCmdkOpen: () => {},
  registerCmdK: () => {},
}

/**
 * Default value is a no-op stub so a component rendered outside the
 * shell (e.g. a unit test) doesn't crash on `toast()` calls. The real
 * value is supplied by {@link AdminShell}.
 */
export const ShellContext: Context<ShellContextValue> =
  createContext<ShellContextValue>(fallback)
